import React, { useMemo, useState, useCallback, useEffect, useRef } from 'react';
import axios from 'axios';
import { FileText, Search, FileDown, Camera, Loader2, Eye, Shield, ChevronDown, ChevronRight, CheckCircle2 } from 'lucide-react';
import toast from 'react-hot-toast';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { formatCertificateDate } from '../utils/custodyChain';
import { useDownloadManagerContext } from '../contexts/DownloadManagerContext';
import './InventoryPage.css';
import './DownloadCertificatesPage.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3009/api/v1';

/** Mensaje desde Grabaciones tras “Guardar y Descargar” en el modal SPOA */
const MSG_SPOA_SAVE_EXPORT = 'vms-spoa-save-export';

interface RecordDownloadListItemDto {
  id: number;
  logicalName: string;
  spoa: string | null;
  codPolicia: string | null;
  siteId: number | null;
  cameraIdNum: number | null;
  cameraName: string | null;
  userId: number | null;
  siteName: string | null;
  siteAddress: string | null;
  userLabel: string | null;
  initialDate: string | null;
  finalDate: string | null;
  createdAt: string;
  downloadStatus: string;
  source: 'export' | 'spoa';
  photoCount?: number;
}

interface CertificateRow {
  id: string;
  spoa: string;
  siteName: string;
  address: string;
  cameraName: string;
  investigatorName: string;
  createdAt: string;
  siteId: number | null;
  cameraIdNum: number | null;
  segmentStart: string | null;
  segmentEnd: string | null;
  source: 'export' | 'spoa';
  photoCount: number;
}

interface SpoaGroup {
  key: string;
  spoa: string;
  latestDate: string;
  rows: CertificateRow[];
}

function secondaryHintForLoadError(message: string): string | null {
  const m = message.toLowerCase();
  if (message.startsWith('Inicie sesión')) return null;
  if (
    m.includes('network error') ||
    m.includes('err_network') ||
    m.includes('networkerror') ||
    m.includes('failed to fetch') ||
    m.includes('econnrefused') ||
    m.includes('enotfound')
  ) {
    return 'No se pudo contactar al API. Compruebe conectividad, VPN, firewall y que la URL del backend sea la correcta.';
  }
  if (m.includes('timeout') || m.includes('timed out') || m.includes('etimedout')) {
    return 'La petición expiró. Compruebe latencia, carga del servidor o límites de tiempo en proxy/firewall.';
  }
  return null;
}

function mapDtoToRow(d: RecordDownloadListItemDto): CertificateRow {
  return {
    id: `${d.source}_${d.id}`,
    spoa: d.spoa?.trim() || '—',
    siteName: d.siteName?.trim() || '—',
    address: d.siteAddress?.trim() || '—',
    cameraName: d.cameraName?.trim() || '—',
    investigatorName: d.userLabel?.trim() || '—',
    createdAt: d.createdAt,
    siteId: d.siteId,
    cameraIdNum: d.cameraIdNum,
    segmentStart: d.initialDate,
    segmentEnd: d.finalDate,
    source: d.source,
    photoCount: d.photoCount ?? 0,
  };
}

/** row.id es `export_<record_download.id>` o `spoa_<negativeEvidenceId>` */
function parseRowBackendIds(rowId: string): {
  source: 'export' | 'spoa';
  recordDownloadId?: number;
  evidenceId?: number;
} {
  const u = rowId.indexOf('_');
  const sourceStr = rowId.slice(0, u);
  const source = sourceStr === 'spoa' ? 'spoa' : 'export';
  const raw = rowId.slice(u + 1);
  const num = Number(raw);
  if (source === 'export') {
    return { source: 'export', recordDownloadId: Number.isFinite(num) ? num : undefined };
  }
  return { source: 'spoa', evidenceId: Number.isFinite(num) ? Math.abs(num) : undefined };
}

async function fetchCertificateRows(token: string): Promise<CertificateRow[]> {
  const res = await axios.get<{ success?: boolean; data?: RecordDownloadListItemDto[] }>(
    `${API_URL}/recordings/downloads`,
    { headers: { Authorization: `Bearer ${token}` } }
  );
  const list = res.data?.data ?? [];
  return list.map(mapDtoToRow);
}

type SpoaAwaitUi =
  | null
  | { phase: 'waiting' | 'listing' | 'zip'; title: string; subtitle?: string };

const DownloadCertificatesPage: React.FC = () => {
  const { token } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const awaitSpoaSave = searchParams.get('awaitSpoaSave') === '1';
  /** Expediente esperado (viene desde Grabaciones al abrir la pestaña); permite descarga automática sin depender solo de postMessage. */
  const expectSpoa = searchParams.get('expectSpoa')?.trim() ?? '';
  const [query, setQuery] = useState('');
  const [rows, setRows] = useState<CertificateRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [previewingIds, setPreviewingIds] = useState<Set<string>>(new Set());
  const [spoaAwait, setSpoaAwait] = useState<SpoaAwaitUi>(null);
  const mountedRef = useRef(true);
  const spoaAutoZipStartedRef = useRef(false);

  const { downloads, addDownload } = useDownloadManagerContext();
  const [rowToDownloadId, setRowToDownloadId] = useState<Record<string, string>>({});

  const isRowDownloading = useCallback((rowId: string) => {
    const dlId = rowToDownloadId[rowId];
    if (!dlId) return false;
    const dl = downloads.find(d => d.id === dlId);
    return dl != null && (dl.status === 'pending' || dl.status === 'preparing' || dl.status === 'downloading');
  }, [rowToDownloadId, downloads]);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const refresh = useCallback(async () => {
    if (!token) {
      setRows([]);
      setLoading(false);
      setLoadError('Inicie sesión para ver las descargas.');
      return;
    }
    setLoading(true);
    setLoadError(null);
    try {
      const mapped = await fetchCertificateRows(token);
      setRows(mapped);
    } catch (e: unknown) {
      let msg = 'No se pudo cargar el listado';
      if (axios.isAxiosError(e)) {
        const d = e.response?.data as { error?: { message?: string } | string; message?: string } | undefined;
        if (d?.error != null) {
          msg =
            typeof d.error === 'object' && d.error !== null && 'message' in d.error
              ? String((d.error as { message: string }).message)
              : String(d.error);
        } else if (d?.message) msg = String(d.message);
        else if (e.message) msg = e.message;
      } else if (e instanceof Error) msg = e.message;
      setLoadError(msg);
      setRows([]);
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  /** Flujo desde Grabaciones: si no hay expectSpoa en URL, overlay “esperando” hasta postMessage */
  useEffect(() => {
    if (!awaitSpoaSave) {
      setSpoaAwait(null);
      return;
    }
    if (expectSpoa) {
      return;
    }
    setSpoaAwait({
      phase: 'waiting',
      title: 'Esperando guardado…',
      subtitle:
        'Cuando termine el guardado en Grabaciones, se actualizará el listado y se generará el paquete descargable.',
    });
  }, [awaitSpoaSave, expectSpoa]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter(
      (r) =>
        r.spoa.toLowerCase().includes(q) ||
        r.siteName.toLowerCase().includes(q) ||
        r.address.toLowerCase().includes(q) ||
        r.cameraName.toLowerCase().includes(q) ||
        r.investigatorName.toLowerCase().includes(q)
    );
  }, [query, rows]);

  const spoaGroups = useMemo((): SpoaGroup[] => {
    const map = new Map<string, SpoaGroup>();
    for (const row of filtered) {
      const key = row.spoa;
      let group = map.get(key);
      if (!group) {
        group = { key, spoa: row.spoa, latestDate: row.createdAt, rows: [] };
        map.set(key, group);
      }
      group.rows.push(row);
      if (row.createdAt > group.latestDate) group.latestDate = row.createdAt;
    }
    return Array.from(map.values());
  }, [filtered]);

  const [expandedSpoas, setExpandedSpoas] = useState<Set<string>>(new Set());

  const toggleSpoa = useCallback((key: string) => {
    setExpandedSpoas(prev => {
      const next = new Set(prev);
      if (next.has(key)) next.delete(key);
      else next.add(key);
      return next;
    });
  }, []);

  const loadErrorSecondaryHint = loadError ? secondaryHintForLoadError(loadError) : null;

  const queueDownloadForRow = useCallback(
    (row: CertificateRow) => {
      if (!row.siteId || !row.cameraIdNum || !row.segmentStart || !row.segmentEnd) {
        toast.error('Datos insuficientes para generar la descarga (sitio, cámara o rango de tiempo faltante).', {
          duration: 4000,
        });
        return;
      }
      const dlId = addDownload({
        type: 'advanced',
        cameraId: row.id,
        cameraName: row.spoa !== '—' ? `SPOA ${row.spoa}` : row.siteName,
        siteId: row.siteId,
        cameraIdNum: row.cameraIdNum,
        startTime: new Date(row.segmentStart),
        endTime: new Date(row.segmentEnd),
        quality: 'original',
        spoa: row.spoa !== '—' ? row.spoa : undefined,
      });
      setRowToDownloadId(prev => ({ ...prev, [row.id]: dlId }));
      toast.success('Descarga agregada a la cola.', { duration: 3000 });
    },
    [addDownload]
  );

  /** Tras guardar SPOA en Grabaciones: refrescar lista y expandir el grupo SPOA para que el usuario descargue manualmente cada evidencia. */
  const showSpoaAfterSave = useCallback(
    async (spoaKey: string) => {
      const key = spoaKey.trim();
      if (!key || !token) {
        setSpoaAwait(null);
        navigate('/download-certificates', { replace: true });
        await refresh();
        return;
      }
      if (spoaAutoZipStartedRef.current) return;
      spoaAutoZipStartedRef.current = true;

      setSpoaAwait({
        phase: 'listing',
        title: 'Cargando evidencias…',
        subtitle: `SPOA ${key}`,
      });

      for (let attempt = 0; attempt < 20; attempt++) {
        if (!mountedRef.current) return;
        try {
          const fresh = await fetchCertificateRows(token);
          if (mountedRef.current) setRows(fresh);
          const found = fresh.some(
            (r) => r.source === 'spoa' && r.spoa.trim().toLowerCase() === key.toLowerCase()
          );
          if (found) break;
        } catch { /* reintentar */ }
        await new Promise((r) => setTimeout(r, 400));
      }

      setSpoaAwait(null);
      setExpandedSpoas((prev) => new Set(prev).add(key));
      navigate('/download-certificates', { replace: true });
    },
    [token, navigate, refresh]
  );

  useEffect(() => {
    if (!awaitSpoaSave || !expectSpoa || !token) return undefined;
    void showSpoaAfterSave(expectSpoa);
  }, [awaitSpoaSave, expectSpoa, token, showSpoaAfterSave]);

  const handleDownload = useCallback(
    (row: CertificateRow) => {
      queueDownloadForRow(row);
    },
    [queueDownloadForRow]
  );

  const openPdfPreview = useCallback(
    async (row: CertificateRow, kind: 'report' | 'custody') => {
      if (!token) {
        toast.error('Inicie sesión para previsualizar.', { duration: 4000 });
        return;
      }

      const ids = parseRowBackendIds(row.id);
      const previewKey = `${row.id}:${kind}`;
      const params: Record<string, string | number> = { source: ids.source, kind };
      if (ids.source === 'export' && ids.recordDownloadId != null) {
        params.recordDownloadId = ids.recordDownloadId;
      }
      if (ids.source === 'spoa' && ids.evidenceId != null) {
        params.evidenceId = ids.evidenceId;
      }

      setPreviewingIds((prev) => new Set(prev).add(previewKey));
      try {
        const res = await axios.get<Blob>(`${API_URL}/recordings/downloads/preview`, {
          params,
          responseType: 'blob',
          headers: { Authorization: `Bearer ${token}` },
          validateStatus: () => true,
        });

        const ct = String(res.headers['content-type'] || '').split(';')[0]?.trim() || '';
        if (res.status !== 200 || !ct.includes('pdf')) {
          let msg = `Error ${res.status}`;
          try {
            const txt = await (res.data as Blob).text();
            const j = JSON.parse(txt) as { error?: { message?: string } | string; message?: string };
            msg =
              (typeof j.error === 'object' && j.error && 'message' in j.error
                ? String((j.error as { message: string }).message)
                : typeof j.error === 'string'
                  ? j.error
                  : '') ||
              (typeof j.message === 'string' ? j.message : '') ||
              msg;
          } catch {
            /* cuerpo no JSON */
          }
          toast.error(msg, { duration: 6000 });
          return;
        }

        const pkcs7 = String(res.headers['x-certificate-pkcs7'] || '').toLowerCase();
        if (kind === 'custody' && pkcs7 === 'applied') {
          toast.success(
            'PDF con sello visual y firma PKCS#7. En el navegador no se ve el panel de firmas; descargue y abra en Adobe Reader para validar Certicámara.',
            { duration: 8000 }
          );
        } else if (kind === 'custody' && pkcs7 === 'none') {
          toast.error(
            'El PDF se generó sin firma PKCS#7 embebida. Revise CERTIFICATE_PATH y CERTIFICATE_PASSWORD en el servidor.',
            { duration: 8000 }
          );
        }

        const url = URL.createObjectURL(res.data);
        window.open(url, '_blank', 'noopener,noreferrer');
        setTimeout(() => URL.revokeObjectURL(url), 120_000);
      } catch (err: unknown) {
        const msg = err instanceof Error ? err.message : 'No se pudo cargar la vista previa';
        toast.error(msg, { duration: 5000 });
      } finally {
        setPreviewingIds((prev) => {
          const next = new Set(prev);
          next.delete(previewKey);
          return next;
        });
      }
    },
    [token]
  );

  /** Coordinación postMessage desde el modal SPOA (Grabaciones). */
  useEffect(() => {
    if (!awaitSpoaSave) return undefined;

    const origin = window.location.origin;

    const onMsg = (e: MessageEvent) => {
      if (e.origin !== origin) return;
      const d = e.data as { type?: string; ok?: boolean; spoa?: string; error?: string };
      if (d?.type !== MSG_SPOA_SAVE_EXPORT) return;

      void (async () => {
        if (!d.ok) {
          toast.error(d.error ?? 'No se guardó la evidencia.', { duration: 6000 });
          setSpoaAwait(null);
          spoaAutoZipStartedRef.current = false;
          navigate('/download-certificates', { replace: true });
          await refresh();
          return;
        }

        const spoaKey = String(d.spoa ?? '').trim();
        await showSpoaAfterSave(spoaKey);
      })();
    };

    window.addEventListener('message', onMsg);
    return () => window.removeEventListener('message', onMsg);
  }, [awaitSpoaSave, navigate, refresh, showSpoaAfterSave]);

  const spoaCount = useMemo(() => rows.filter((r) => r.source === 'spoa').length, [rows]);
  const exportCount = useMemo(() => rows.filter((r) => r.source === 'export').length, [rows]);

  return (
    <div className="inventory-page download-certificates-page">
      <div className="page-header">
        <div className="header-title">
          <FileText size={32} className="header-icon" aria-hidden />
          <div>
            <h1>Descarga de Evidencias</h1>
            <p>
              {loading
                ? 'Cargando…'
                : `${filtered.length} registro${filtered.length === 1 ? '' : 's'}` +
                  (spoaCount > 0 ? ` · ${spoaCount} SPOA` : '') +
                  (exportCount > 0 ? ` · ${exportCount} exportacion${exportCount === 1 ? '' : 'es'}` : '')
              }
            </p>
          </div>
        </div>
      </div>

      <div className="dc-search-wrap">
        <div className="dc-search">
          <Search size={20} aria-hidden />
          <input
            type="search"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por SPOA, cámara, sitio, dirección o solicitante…"
            aria-label="Buscar descargas"
            disabled={loading || !!loadError}
          />
        </div>
      </div>

      <section className="dc-table-section" aria-label="Descargas por SPOA">
        {loadError ? (
          <div className="dc-empty dc-empty--error">
            <p>{loadError}</p>
            {loadErrorSecondaryHint ? <p className="dc-empty-hint">{loadErrorSecondaryHint}</p> : null}
            <button type="button" className="dc-retry-btn" onClick={() => void refresh()}>
              Reintentar
            </button>
          </div>
        ) : loading ? (
          <div className="dc-empty">Cargando descargas…</div>
        ) : filtered.length === 0 ? (
          <div className="dc-empty">
            {rows.length === 0
              ? 'No hay registros. Al crear un SPOA o exportar un clip con certificado desde Grabaciones, el registro aparecerá aquí.'
              : 'No hay resultados para la búsqueda.'}
          </div>
        ) : (
          <div className="dc-table-scroll">
            <table className="dc-table dc-table--compact">
            <thead>
              <tr>
                <th className="dc-cell-icon" aria-hidden />
                <th>Cámara</th>
                <th>Sitio</th>
                <th>Tipo</th>
                <th>Fecha</th>
                <th className="dc-th-preview">Vista previa</th>
                <th className="dc-th-download">Descargar</th>
              </tr>
            </thead>
            <tbody>
              {spoaGroups.map((group) => {
                const isExpanded = expandedSpoas.has(group.key);
                return (
                  <React.Fragment key={group.key}>
                    <tr
                      className="dc-spoa-header"
                      onClick={() => toggleSpoa(group.key)}
                      role="button"
                      tabIndex={0}
                      onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); toggleSpoa(group.key); } }}
                      aria-expanded={isExpanded}
                    >
                      <td className="dc-cell-icon">
                        {isExpanded
                          ? <ChevronDown size={18} aria-hidden />
                          : <ChevronRight size={18} aria-hidden />
                        }
                      </td>
                      <td colSpan={6}>
                        <div className="dc-spoa-header-content">
                          <FileText size={16} className="dc-spoa-header-icon" aria-hidden />
                          <span className="dc-spoa-header-name">{group.spoa}</span>
                          <span className="dc-spoa-header-date">{formatCertificateDate(group.latestDate)}</span>
                          <span className="dc-spoa-header-count">
                            {group.rows.length} evidencia{group.rows.length > 1 ? 's' : ''}
                          </span>
                        </div>
                      </td>
                    </tr>
                    {isExpanded && group.rows.map((row) => {
                      const canDownload = !!row.siteId && !!row.cameraIdNum && !!row.segmentStart && !!row.segmentEnd;
                      const hasExpediente = row.spoa.trim() !== '' && row.spoa !== '—';
                      const showConstanciaPreview = row.source === 'export';
                      const showCustodiaPreview = row.source === 'spoa' || (row.source === 'export' && hasExpediente);
                      return (
                        <tr key={row.id} className={`dc-evidence-row ${row.source === 'spoa' ? 'dc-row--spoa' : ''}`}>
                          <td className="dc-cell-icon">
                            <CheckCircle2 size={16} className="dc-check-icon" aria-hidden />
                          </td>
                          <td>
                            <div className="dc-evidence-camera">
                              <Camera size={14} aria-hidden />
                              <span>{row.cameraName}</span>
                            </div>
                            {row.source === 'spoa' && row.photoCount > 0 && (
                              <span className="dc-photo-badge">{row.photoCount} foto{row.photoCount > 1 ? 's' : ''}</span>
                            )}
                          </td>
                          <td>{row.siteName}</td>
                          <td>
                            <span className="dc-type-tag dc-type-tag--spoa">SPOA</span>
                          </td>
                          <td>{formatCertificateDate(row.createdAt)}</td>
                          <td className="dc-cell-preview">
                            <div className="dc-preview-btns">
                              {showConstanciaPreview ? (
                                <button
                                  type="button"
                                  className="dc-preview-btn"
                                  title="Abrir constancia de descarga (PDF)"
                                  aria-label={`Vista previa constancia ${row.spoa}`}
                                  disabled={previewingIds.has(`${row.id}:report`)}
                                  onClick={(e) => { e.stopPropagation(); void openPdfPreview(row, 'report'); }}
                                >
                                  {previewingIds.has(`${row.id}:report`) ? (
                                    <Loader2 size={16} className="dc-spin" aria-hidden />
                                  ) : (
                                    <Eye size={16} aria-hidden />
                                  )}
                                  <span>Constancia</span>
                                </button>
                              ) : null}
                              {showCustodiaPreview ? (
                                <button
                                  type="button"
                                  className="dc-preview-btn dc-preview-btn--custody"
                                  title="Abrir certificado de cadena de custodia (PDF)"
                                  aria-label={`Vista previa custodia ${row.spoa}`}
                                  disabled={previewingIds.has(`${row.id}:custody`)}
                                  onClick={(e) => { e.stopPropagation(); void openPdfPreview(row, 'custody'); }}
                                >
                                  {previewingIds.has(`${row.id}:custody`) ? (
                                    <Loader2 size={16} className="dc-spin" aria-hidden />
                                  ) : (
                                    <Shield size={16} aria-hidden />
                                  )}
                                  <span>Custodia</span>
                                </button>
                              ) : null}
                            </div>
                          </td>
                          <td className="dc-cell-download">
                            <button
                              type="button"
                              className="dc-download-btn"
                              title={canDownload ? 'Descargar video + certificado' : 'Datos insuficientes para descarga'}
                              aria-label={`Descargar SPOA ${row.spoa}`}
                              onClick={(e) => { e.stopPropagation(); handleDownload(row); }}
                              disabled={isRowDownloading(row.id) || !canDownload}
                            >
                              {isRowDownloading(row.id)
                                ? <Loader2 size={18} className="dc-spin" aria-hidden />
                                : <FileDown size={18} aria-hidden />
                              }
                              <span>{isRowDownloading(row.id) ? 'En cola…' : 'Descargar'}</span>
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </section>

      {spoaAwait ? (
        <div className="dc-spoa-await-overlay" role="alertdialog" aria-busy="true" aria-live="polite">
          <div className="dc-spoa-await-card">
            <Loader2 className="dc-spin dc-spoa-await-loader" size={44} aria-hidden />
            <p className="dc-spoa-await-title">{spoaAwait.title}</p>
            {spoaAwait.subtitle ? <p className="dc-spoa-await-sub">{spoaAwait.subtitle}</p> : null}
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default DownloadCertificatesPage;
